import featureFlagsJsonData from '@/assets/json/featureFlags.json';

interface FeatureFlagsFromJson {
  containment_docs_active: boolean;
  containment_terms_active: boolean;
  visitor_geolocation_popup_active: boolean;
  check_user_inactivity_active: boolean;
  contracts_modal_active: boolean;
  sms_integration_active: boolean;
  bank_account_active: boolean;
  redeemable_cashback_active: boolean;
  redeemable_rewards_active: boolean;
  centralized_exclusion_active: boolean;
  game_statistics_period: string;
  game_statistics_items: string[];
  user_refers_active: boolean;
  user_refers_coming_soon: boolean;
  footer_v2_active: boolean;
  user_v2_active: boolean;
  club_fidelidade_active: boolean;
  play_with_bonus_active: boolean;
}

export default function useFeatureFlagsJson() {
  const featureFlagsJson: FeatureFlagsFromJson = featureFlagsJsonData;

  // FUNCTIONS

  function isFeatureFromJsonEnabled(key: keyof FeatureFlagsFromJson): boolean {
    const featureFlag = featureFlagsJson[key];

    if (!key || !featureFlag) {
      return false;
    }

    return !!featureFlag || false;
  }

  // FEATURES

  const isContainmentDocsEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('containment_docs_active');
    // return isFeatureFromApiEnabled('containment_docs_active');
  });

  const isContainmentTermsEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('containment_terms_active');
    // return isFeatureFromApiEnabled('containment_terms_active');
  });

  const isGeoLocationVisitorEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('visitor_geolocation_popup_active');
    // return isFeatureFromApiEnabled('visitor_geolocation_popup_active');
  });

  const isCheckInactivityEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('check_user_inactivity_active');
    // return isFeatureFromApiEnabled('check_user_inactivity_active');
  });

  const isContractsModalEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('contracts_modal_active');
  });

  const isSmsIntegrationEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('sms_integration_active');
  });

  const isBankAccountEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('bank_account_active');
  });

  const isRedeemableCashbackEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('redeemable_cashback_active');
  });

  const isRedeemableRewardsEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('redeemable_rewards_active');
  });

  const isCentralizedExclusionEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('centralized_exclusion_active');
  });

  const gameStatisticsPeriod = computed<string>(() => {
    return featureFlagsJson.game_statistics_period || 'last_24_hours';
  });

  const gameStatisticsItems = computed<string[]>(() => {
    return featureFlagsJson.game_statistics_items || ['wins', 'players', 'rtp'];
  });

  const isUserRefersEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('user_refers_active');
  });

  // Indique e Ganhe temporariamente "Em breve": quando true, os pontos de
  // entrada NÃO somem — ficam renderizados com blur + cadeado ("Em breve"),
  // com o click desativado, e a rota /user/refers redireciona pra home.
  // Reativar = voltar user_refers_coming_soon pra false no featureFlags.json.
  const isUserRefersComingSoon = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('user_refers_coming_soon');
  });

  const isFooterV2Enabled = computed<boolean>(() => {
    const { featureFlags } = useRuntimeConfig().public;

    if (featureFlags.footerV2Active) return true;

    return isFeatureFromJsonEnabled('footer_v2_active');
  });

  const isUserV2Enabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('user_v2_active');
  });

  const isClubFidelidadeEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('club_fidelidade_active');
  });

  // Jogar com Bonus (selecao de carteira por sessao). OFF = fluxo atual
  // intacto: o gate de carteira nem monta e o launch nao manda wallet_type
  // (fallback legado do core). Rollout 5 -> 25 -> 100% e so ligar aqui.
  const isPlayWithBonusEnabled = computed<boolean>(() => {
    return isFeatureFromJsonEnabled('play_with_bonus_active');
  });

  return {
    isUserV2Enabled,
    isContainmentDocsEnabled,
    isContainmentTermsEnabled,
    isGeoLocationVisitorEnabled,
    isCheckInactivityEnabled,
    isContractsModalEnabled,
    isSmsIntegrationEnabled,
    isBankAccountEnabled,
    isRedeemableCashbackEnabled,
    isRedeemableRewardsEnabled,
    isCentralizedExclusionEnabled,
    gameStatisticsPeriod,
    gameStatisticsItems,
    isUserRefersEnabled,
    isUserRefersComingSoon,
    isFooterV2Enabled,
    isClubFidelidadeEnabled,
    isPlayWithBonusEnabled,
  };
}
